import {useMutation, useQueryClient} from "@tanstack/react-query";
import {AxiosError} from "axios";

import {errorHandler} from "@/src/modules/core/lib";
import {useInvalidate, useToast} from "@/src/modules/core/hooks";
import {captureShiftLocationRejected} from "@/src/modules/core/lib/glitchtip/shifts-capture";

import {isShiftAlreadyStartedError} from "../lib/is-shift-already-started-error";
import {recoverAlreadyStartedShift} from "../lib/shift-start-cache";
import {officerService} from "../services";
import type {ShiftStartRequest, ShiftStartResponseData} from "../types";

export function useShiftStart() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const invalidateMe = useInvalidate("me");
  const invalidateShifts = useInvalidate("shifts");

  return useMutation<ShiftStartResponseData, unknown, ShiftStartRequest>({
    mutationFn: (data) => officerService.shiftStart(data),
    onError: async (error, variables) => {
      if (isShiftAlreadyStartedError(error)) {
        const recovered = await recoverAlreadyStartedShift(queryClient, variables.shift_id);
        if (recovered) {
          toast.info("You're already on this shift");
          invalidateMe();
          invalidateShifts();
          return;
        }
      }
      captureShiftLocationRejected(error, {
        operation: "shift_start",
        shiftId: variables.shift_id,
        gpsSource: variables.gps_source,
      });
      errorHandler(error as AxiosError);
    },
  });
}
